/**
 * Layout shape primitives shared by the store, the App shell, and the
 * header's layout-preset menu. Persisted in localStorage as JSON so the
 * inline init script in index.html can race-free apply preset widths
 * before paint.
 */

export const SIDEBAR_RAIL_WIDTH = 56;
export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 480;
export const SIDEBAR_DEFAULT = 256;

export const RIGHT_PANE_MIN = 360;
export const RIGHT_PANE_MAX = 720;
export const RIGHT_PANE_DEFAULT = 544;

export type LayoutPreset = "default" | "focus" | "chat-heavy" | "code-heavy";

export interface LayoutShape {
  sidebarWidth: number; // width when expanded
  sidebarCollapsed: boolean; // true = rail (SIDEBAR_RAIL_WIDTH)
  rightPaneWidth: number;
  rightPaneVisible: boolean;
  preset: LayoutPreset | null; // null = customized
}

export const LAYOUT_PRESETS: Record<
  LayoutPreset,
  { label: string; shape: Omit<LayoutShape, "preset"> }
> = {
  default: {
    label: "Default",
    shape: {
      sidebarWidth: SIDEBAR_DEFAULT,
      sidebarCollapsed: false,
      rightPaneWidth: RIGHT_PANE_DEFAULT,
      rightPaneVisible: true,
    },
  },
  focus: {
    label: "Focus",
    shape: {
      sidebarWidth: SIDEBAR_DEFAULT,
      sidebarCollapsed: true,
      rightPaneWidth: RIGHT_PANE_DEFAULT,
      rightPaneVisible: false,
    },
  },
  "chat-heavy": {
    label: "Chat-heavy",
    shape: {
      sidebarWidth: SIDEBAR_DEFAULT,
      sidebarCollapsed: true,
      rightPaneWidth: RIGHT_PANE_MAX,
      rightPaneVisible: true,
    },
  },
  "code-heavy": {
    label: "Code-heavy",
    shape: {
      sidebarWidth: 220,
      sidebarCollapsed: false,
      rightPaneWidth: RIGHT_PANE_MAX,
      rightPaneVisible: true,
    },
  },
};

const KEY = "maestro.layout";

export function clampSidebar(w: number): number {
  return Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, Math.round(w)));
}

export function clampRightPane(w: number): number {
  return Math.max(RIGHT_PANE_MIN, Math.min(RIGHT_PANE_MAX, Math.round(w)));
}

export function loadLayout(): LayoutShape {
  if (typeof window === "undefined") {
    return { ...LAYOUT_PRESETS.default.shape, preset: "default" };
  }
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { ...LAYOUT_PRESETS.default.shape, preset: "default" };
    const parsed = JSON.parse(raw) as Partial<LayoutShape>;
    return {
      sidebarWidth: clampSidebar(
        parsed.sidebarWidth ?? LAYOUT_PRESETS.default.shape.sidebarWidth,
      ),
      sidebarCollapsed: Boolean(parsed.sidebarCollapsed),
      rightPaneWidth: clampRightPane(
        parsed.rightPaneWidth ?? LAYOUT_PRESETS.default.shape.rightPaneWidth,
      ),
      rightPaneVisible: parsed.rightPaneVisible ?? true,
      preset: parsed.preset ?? null,
    };
  } catch {
    return { ...LAYOUT_PRESETS.default.shape, preset: "default" };
  }
}

export function saveLayout(layout: LayoutShape): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(layout));
  } catch {
    // localStorage full or disabled — accept silently
  }
}
